"use client";

import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";

const safariStyles = `
  .gradient-text {
    background: linear-gradient(90deg, #6ee7ff, #b26eff, #47ffa1);
    background: -webkit-linear-gradient(90deg, #6ee7ff, #b26eff, #47ffa1);
    background: -moz-linear-gradient(90deg, #6ee7ff, #b26eff, #47ffa1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
  
  @supports not (-webkit-background-clip: text) {
    .gradient-text {
      background: none;
      color: #6ee7ff;
    }
  }
`;

export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const beamTranslate = useTransform(scrollYProgress, [0, 1], [0, -100]);

  useEffect(() => {
    // Inject Safari gradient styles
    const styleElement = document.createElement('style');
    styleElement.textContent = safariStyles;
    document.head.appendChild(styleElement);

    // Simple animated gradient background with canvas
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrame: number;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const animate = () => {
      time += 0.01;

      const gradient = ctx.createRadialGradient(
        canvas.width * 0.3 + Math.sin(time) * 200,
        canvas.height * 0.4 + Math.cos(time * 0.8) * 150,
        0,
        canvas.width * 0.7,
        canvas.height * 0.6,
        canvas.width * 0.6
      );

      gradient.addColorStop(0, `hsla(${190 + Math.sin(time) * 20}, 80%, 70%, 0.1)`);
      gradient.addColorStop(0.5, `hsla(${280 + Math.cos(time * 0.7) * 30}, 70%, 65%, 0.08)`);
      gradient.addColorStop(1, "transparent");

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      animationFrame = requestAnimationFrame(animate);
    };

    resize();
    animate();

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrame);
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ mixBlendMode: "multiply" }}
      />

      {/* Light Beams (parallax) */}
      <motion.div className="absolute inset-0 opacity-30" style={{ y: beamTranslate }} aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-1 h-96 bg-gradient-to-b from-accent to-transparent rotate-12 blur-sm" />
        <div className="absolute top-1/3 right-1/3 w-1 h-80 bg-gradient-to-b from-accent2 to-transparent -rotate-6 blur-sm" />
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold leading-none tracking-tight mb-8">
            <span className="block gradient-text animate-pulse">
              Creative
            </span>
            <span className="block text-foreground font-light">Credentials</span>
          </h1>

          <motion.p
            className="text-lg md:text-xl lg:text-2xl text-foreground/80 mb-4 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Design The Future
          </motion.p>

          <motion.h2
            className="text-2xl md:text-3xl lg:text-4xl font-semibold text-accent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            Shota Iasashvili
          </motion.h2>
        </motion.div>

        {/* Scroll Down Indicator */}
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center text-foreground/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <p className="text-sm mb-2 font-light">Scroll to explore</p>
          <ChevronDown className="w-6 h-6 scroll-cue" />
        </motion.div>
      </div>

      {/* Floating Accents */}
      <motion.div
        className="absolute top-1/4 left-8 w-3 h-3 bg-accent rounded-full blur-sm opacity-60"
        animate={{ y: [0, -20, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />
      <motion.div
        className="absolute top-2/3 right-12 w-2 h-2 bg-accent2 rounded-full blur-sm opacity-50"
        animate={{ y: [0, 15, 0], x: [0, -10, 0], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />
    </section>
  );
}